import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Switch } from '@/components/ui/switch';
import {
  Search,
  Filter,
  Calendar as CalendarIcon,
  X,
  Mail,
  Clock,
  User,
  Paperclip,
} from 'lucide-react';
import { format } from 'date-fns';

export interface EmailFilters {
  searchQuery: string;
  status: 'all' | 'unread' | 'read' | 'sent' | 'draft';
  hasAttachments?: boolean;
  dateRange?: {
    from: Date | undefined;
    to: Date | undefined;
  };
  sender?: string;
  recipient?: string;
  sortBy: 'date' | 'sender' | 'subject';
  sortOrder: 'asc' | 'desc';
}

interface EmailSearchFilterProps {
  filters: EmailFilters;
  onFiltersChange: (filters: EmailFilters) => void;
  onSearch: () => void;
  resultCount?: number;
}

export function EmailSearchFilter({
  filters,
  onFiltersChange,
  onSearch,
  resultCount,
}: EmailSearchFilterProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState<'from' | 'to' | null>(null);

  const activeFiltersCount = [
    filters.status !== 'all',
    filters.hasAttachments,
    filters.dateRange?.from,
    filters.sender,
    filters.recipient,
  ].filter(Boolean).length;

  const clearFilters = () => {
    onFiltersChange({
      searchQuery: '',
      status: 'all',
      hasAttachments: undefined,
      dateRange: undefined,
      sender: undefined,
      recipient: undefined,
      sortBy: 'date',
      sortOrder: 'desc',
    });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <div className="space-y-3">
      {/* Main Search Bar */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search emails... (Press Enter)"
            value={filters.searchQuery}
            onChange={(e) =>
              onFiltersChange({ ...filters, searchQuery: e.target.value })
            }
            onKeyDown={handleSearchKeyDown}
            className="pl-9 pr-10"
          />
          {filters.searchQuery && (
            <button
              onClick={() => onFiltersChange({ ...filters, searchQuery: '' })}
              className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Popover open={showAdvanced} onOpenChange={setShowAdvanced}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="relative">
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {activeFiltersCount > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-2 px-1.5 py-0 h-5 text-xs"
                >
                  {activeFiltersCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Advanced Filters</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="h-auto p-1 text-xs"
                >
                  Clear all
                </Button>
              </div>

              {/* Status Filter */}
              <div className="space-y-2">
                <Label className="text-sm">Status</Label>
                <Select
                  value={filters.status}
                  onValueChange={(value) =>
                    onFiltersChange({
                      ...filters,
                      status: value as EmailFilters['status'],
                    })
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Emails</SelectItem>
                    <SelectItem value="unread">Unread</SelectItem>
                    <SelectItem value="read">Read</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="draft">Drafts</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Has Attachments */}
              <div className="flex items-center justify-between">
                <Label className="text-sm flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Has Attachments
                </Label>
                <Switch
                  checked={filters.hasAttachments || false}
                  onCheckedChange={(checked) =>
                    onFiltersChange({
                      ...filters,
                      hasAttachments: checked || undefined,
                    })
                  }
                />
              </div>

              {/* Date Range */}
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  Date Range
                </Label>
                <div className="flex gap-2">
                  <Popover
                    open={datePickerOpen === 'from'}
                    onOpenChange={(open) => setDatePickerOpen(open ? 'from' : null)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 justify-start text-left font-normal"
                      >
                        {filters.dateRange?.from ? (
                          format(filters.dateRange.from, 'MMM d, yyyy')
                        ) : (
                          <span className="text-muted-foreground">From</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateRange?.from}
                        onSelect={(date) => {
                          onFiltersChange({
                            ...filters,
                            dateRange: {
                              from: date,
                              to: filters.dateRange?.to,
                            },
                          });
                          setDatePickerOpen(null);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover
                    open={datePickerOpen === 'to'}
                    onOpenChange={(open) => setDatePickerOpen(open ? 'to' : null)}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 justify-start text-left font-normal"
                      >
                        {filters.dateRange?.to ? (
                          format(filters.dateRange.to, 'MMM d, yyyy')
                        ) : (
                          <span className="text-muted-foreground">To</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.dateRange?.to}
                        onSelect={(date) => {
                          onFiltersChange({
                            ...filters,
                            dateRange: {
                              from: filters.dateRange?.from,
                              to: date,
                            },
                          });
                          setDatePickerOpen(null);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Sender Filter */}
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <User className="h-4 w-4" />
                  From
                </Label>
                <Input
                  placeholder="Sender email..."
                  value={filters.sender || ''}
                  onChange={(e) =>
                    onFiltersChange({
                      ...filters,
                      sender: e.target.value || undefined,
                    })
                  }
                />
              </div>

              {/* Recipient Filter */}
              <div className="space-y-2">
                <Label className="text-sm flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  To
                </Label>
                <Input
                  placeholder="Recipient email..."
                  value={filters.recipient || ''}
                  onChange={(e) =>
                    onFiltersChange({
                      ...filters,
                      recipient: e.target.value || undefined,
                    })
                  }
                />
              </div>

              {/* Sort Options */}
              <div className="space-y-2">
                <Label className="text-sm">Sort By</Label>
                <div className="flex gap-2">
                  <Select
                    value={filters.sortBy}
                    onValueChange={(value) =>
                      onFiltersChange({
                        ...filters,
                        sortBy: value as EmailFilters['sortBy'],
                      })
                    }
                  >
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="sender">Sender</SelectItem>
                      <SelectItem value="subject">Subject</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.sortOrder}
                    onValueChange={(value) =>
                      onFiltersChange({
                        ...filters,
                        sortOrder: value as EmailFilters['sortOrder'],
                      })
                    }
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Newest</SelectItem>
                      <SelectItem value="asc">Oldest</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Apply Button */}
              <Button onClick={onSearch} className="w-full">
                Apply Filters
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.status !== 'all' && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Mail className="h-3 w-3" />
              {filters.status}
              <button
                onClick={() => onFiltersChange({ ...filters, status: 'all' })}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.hasAttachments && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Paperclip className="h-3 w-3" />
              Has attachments
              <button
                onClick={() =>
                  onFiltersChange({ ...filters, hasAttachments: undefined })
                }
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.dateRange?.from && (
            <Badge variant="secondary" className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {format(filters.dateRange.from, 'MMM d')}
              {filters.dateRange.to && ` - ${format(filters.dateRange.to, 'MMM d')}`}
              <button
                onClick={() =>
                  onFiltersChange({ ...filters, dateRange: undefined })
                }
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.sender && (
            <Badge variant="secondary" className="flex items-center gap-1">
              From: {filters.sender}
              <button
                onClick={() => onFiltersChange({ ...filters, sender: undefined })}
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}

          {filters.recipient && (
            <Badge variant="secondary" className="flex items-center gap-1">
              To: {filters.recipient}
              <button
                onClick={() =>
                  onFiltersChange({ ...filters, recipient: undefined })
                }
                className="ml-1 hover:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {/* Results Count */}
      {resultCount !== undefined && (
        <div className="text-sm text-muted-foreground">
          {resultCount === 0
            ? 'No emails found'
            : `${resultCount} email${resultCount !== 1 ? 's' : ''} found`}
        </div>
      )}
    </div>
  );
}